import { useQuery, useMutation, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../lib/api';
import { authService } from './authService';
import { ApiResponse, SuccessResponse, Pagination } from '../types/api';
import type { ChangePasswordData } from '../types/auth';
import {
  GeneralSettings,
  UpdateGeneralSettingsData,
  TeamMember,
  TeamListResponse,
  TeamQueryParams,
  InviteTeamMemberData,
  UpdateTeamMemberRoleData,
  TransferOwnershipData,
  NotificationPreferences,
  UpdateNotificationPreferencesData,
  SecuritySettings,
  SSOConfig,
  UpdateSSOConfigData,
  LearnerAttribute,
  LearnerAttributeCreate,
  LearnerAttributeUpdate,
  PaginatedAttributes,
  LearnerAttributeKind,
} from '../types/settings';

// ─── General Settings ───────────────────────────────────────────────────

/**
 * Get general tenant settings
 * GET /api/v1/settings/general
 */
export const fetchGeneralSettings = async (): Promise<GeneralSettings> => {
  const response = await api.get<ApiResponse<GeneralSettings>>('/settings/general');
  return response.data.data!;
};

export const useGeneralSettings = (): UseQueryResult<GeneralSettings, Error> => {
  return useQuery({
    queryKey: ['settings', 'general'],
    queryFn: fetchGeneralSettings,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Update general settings
 * PUT /api/v1/settings/general
 */
export const updateGeneralSettings = async (
  data: UpdateGeneralSettingsData
): Promise<SuccessResponse> => {
  const response = await api.put<{ data: SuccessResponse }>('/settings/general', data);
  return response.data.data;
};

export const useUpdateGeneralSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateGeneralSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'general'] });
    },
  });
};

// ─── Team ───────────────────────────────────────────────────────────────

/**
 * List team members
 * GET /api/v1/settings/team
 */
export const fetchTeamMembers = async (
  params: TeamQueryParams = {}
): Promise<{ members: TeamMember[]; pagination: Pagination }> => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append('page', params.page.toString());
  if (params.limit) queryParams.append('limit', params.limit.toString());

  const response = await api.get<TeamListResponse>(`/settings/team?${queryParams.toString()}`);
  const payload = response.data.data;
  return { members: payload?.members ?? [], pagination: payload?.pagination };
};

export const useTeamMembers = (
  params: TeamQueryParams = {}
): UseQueryResult<{ members: TeamMember[]; pagination: Pagination }, Error> => {
  return useQuery({
    queryKey: ['settings', 'team', params],
    queryFn: () => fetchTeamMembers(params),
    staleTime: 2 * 60 * 1000,
  });
};

/**
 * Invite member to tenant
 * POST /api/v1/settings/team/invite
 */
export const inviteTeamMember = async (data: InviteTeamMemberData): Promise<SuccessResponse> => {
  const response = await api.post<{ data: SuccessResponse }>('/settings/team/invite', data);
  return response.data.data;
};

export const useInviteTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: inviteTeamMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'team'] });
    },
  });
};

/**
 * Update team member role
 * PUT /api/v1/settings/team/:userId/role
 */
export const updateTeamMemberRole = async (
  userId: string,
  data: UpdateTeamMemberRoleData
): Promise<SuccessResponse> => {
  const response = await api.put<{ data: SuccessResponse }>(
    `/settings/team/${userId}/role`,
    data
  );
  return response.data.data;
};

export const useUpdateTeamMemberRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateTeamMemberRoleData }) =>
      updateTeamMemberRole(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'team'] });
    },
  });
};

/**
 * Remove team member
 * DELETE /api/v1/settings/team/:userId
 */
export const removeTeamMember = async (userId: string): Promise<SuccessResponse> => {
  const response = await api.delete<{ data: SuccessResponse }>(`/settings/team/${userId}`);
  return response.data.data;
};

export const useRemoveTeamMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeTeamMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'team'] });
    },
  });
};

/**
 * Transfer tenant ownership
 * POST /api/v1/settings/team/transfer-ownership
 */
export const transferOwnership = async (
  data: TransferOwnershipData
): Promise<SuccessResponse> => {
  const response = await api.post<{ data: SuccessResponse }>(
    '/settings/team/transfer-ownership',
    data
  );
  return response.data.data;
};

export const useTransferOwnership = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: transferOwnership,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'team'] });
    },
  });
};

// ─── Notification Preferences ───────────────────────────────────────────

/**
 * Get notification preferences
 * GET /api/v1/settings/notifications
 */
export const fetchNotificationPreferences = async (): Promise<NotificationPreferences> => {
  const response = await api.get<ApiResponse<NotificationPreferences>>('/settings/notifications');
  return response.data.data!;
};

export const useNotificationPreferences = (): UseQueryResult<NotificationPreferences, Error> => {
  return useQuery({
    queryKey: ['settings', 'notifications'],
    queryFn: fetchNotificationPreferences,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Update notification preferences
 * PUT /api/v1/settings/notifications
 */
export const updateNotificationPreferences = async (
  data: UpdateNotificationPreferencesData
): Promise<NotificationPreferences> => {
  const response = await api.put<ApiResponse<NotificationPreferences>>(
    '/settings/notifications',
    data
  );
  return response.data.data!;
};

export const useUpdateNotificationPreferences = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateNotificationPreferences,
    onSuccess: (data) => {
      queryClient.setQueryData(['settings', 'notifications'], data);
      toast.success('Notification preferences updated');
    },
    onError: () => {
      toast.error('Could not update your notification preferences. Please try again.');
    },
  });
};

// ─── Security ───────────────────────────────────────────────────────────

/**
 * Get security settings
 * GET /api/v1/settings/security
 */
export const fetchSecuritySettings = async (): Promise<SecuritySettings> => {
  const response = await api.get<ApiResponse<SecuritySettings>>('/settings/security');
  return response.data.data!;
};

export const useSecuritySettings = (): UseQueryResult<SecuritySettings, Error> => {
  return useQuery({
    queryKey: ['settings', 'security'],
    queryFn: fetchSecuritySettings,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Change the current user's password.
 * POST /api/v1/auth/change-password
 *
 * Wraps the auth service so the Security tab stays on the data-flow contract
 * (component → hook → service → axios). Refetches the security overview on
 * success so derived fields (e.g. last password change) stay current.
 */
export const useChangePassword = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ChangePasswordData) => authService.changePassword(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'security'] });
    },
  });
};

/**
 * Get SSO configuration
 * GET /api/v1/settings/security/sso
 */
export const fetchSSOConfig = async (): Promise<SSOConfig> => {
  const response = await api.get<ApiResponse<SSOConfig>>('/settings/security/sso');
  return response.data.data!;
};

export const useSSOConfig = (): UseQueryResult<SSOConfig, Error> => {
  return useQuery({
    queryKey: ['settings', 'security', 'sso'],
    queryFn: fetchSSOConfig,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Update SSO configuration
 * PUT /api/v1/settings/security/sso
 */
export const updateSSOConfig = async (data: UpdateSSOConfigData): Promise<SuccessResponse> => {
  const response = await api.put<{ data: SuccessResponse }>('/settings/security/sso', data);
  return response.data.data;
};

export const useUpdateSSOConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSSOConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'security', 'sso'] });
    },
  });
};

// ─── Learner Attributes (Roles / Departments / Locations) ───────────────
// All three attribute types share the same schema and CRUD pattern.
// Endpoints: /settings/learners/{kind}  and  /settings/learners/{kind}/{id}

export interface ListAttributesParams {
  page?: number;
  per_page?: number;
}

export const fetchLearnerAttributes = async (
  kind: LearnerAttributeKind,
  params: ListAttributesParams = {}
): Promise<PaginatedAttributes> => {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.per_page) qs.set('per_page', String(params.per_page));
  const response = await api.get<PaginatedAttributes>(
    `/settings/learners/${kind}?${qs.toString()}`
  );
  return response.data;
};

export const useLearnerAttributes = (
  kind: LearnerAttributeKind,
  params: ListAttributesParams = {}
): UseQueryResult<PaginatedAttributes, Error> =>
  useQuery({
    queryKey: ['settings', 'learners', kind, params],
    queryFn: () => fetchLearnerAttributes(kind, params),
    staleTime: 2 * 60 * 1000,
  });

export const createLearnerAttribute = async (
  kind: LearnerAttributeKind,
  data: LearnerAttributeCreate
): Promise<LearnerAttribute> => {
  const response = await api.post<LearnerAttribute>(`/settings/learners/${kind}`, data);
  return response.data;
};

export const useCreateLearnerAttribute = (kind: LearnerAttributeKind) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: LearnerAttributeCreate) => createLearnerAttribute(kind, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'learners', kind] });
    },
  });
};

export const updateLearnerAttribute = async (
  kind: LearnerAttributeKind,
  id: string,
  data: LearnerAttributeUpdate
): Promise<LearnerAttribute> => {
  const response = await api.put<LearnerAttribute>(`/settings/learners/${kind}/${id}`, data);
  return response.data;
};

export const useUpdateLearnerAttribute = (kind: LearnerAttributeKind) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LearnerAttributeUpdate }) =>
      updateLearnerAttribute(kind, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'learners', kind] });
    },
  });
};

export const deleteLearnerAttribute = async (
  kind: LearnerAttributeKind,
  id: string
): Promise<void> => {
  await api.delete(`/settings/learners/${kind}/${id}`);
};

export const useDeleteLearnerAttribute = (kind: LearnerAttributeKind) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteLearnerAttribute(kind, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'learners', kind] });
    },
  });
};

// ─── Workspace branding (Pro: logo + color; Enterprise: + white label) ──────

export interface BrandingUpdate {
  primary_color?: string | null;
  secondary_color?: string | null;
  logo_key?: string | null;
  white_label?: boolean;
}

export const updateBranding = async (
  update: BrandingUpdate
): Promise<{ styling: Record<string, unknown> }> => {
  const response = await api.put<{ data: { styling: Record<string, unknown> } }>(
    '/settings/branding',
    update
  );
  return response.data.data;
};

export const useUpdateBranding = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateBranding,
    onSuccess: () => {
      // Branding rides on the auth payload; the caller refreshes the session.
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
  });
};
